import { useState } from 'react';
import {
  Box,
  Image,
  Heading,
  Flex,
  Spacer,
  Stack,
  Icon,
  Text,
  Tag,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Tooltip,
  useColorModeValue,
  Button,
  Spinner
} from '@chakra-ui/react';
import { ListingModel, ItemModel } from 'models';
import { ListingStatus } from 'models/Listing/types';
import { IconType } from 'react-icons';
import { BsFillArrowRightCircleFill, BsTrashFill } from 'react-icons/bs';
import { RiAuctionLine } from 'react-icons/ri';
import { MdPendingActions, MdGroupWork, MdFilter, MdCropOriginal, MdWarning, MdLinkOff, MdCheck } from 'react-icons/md';
import { BiRuler } from 'react-icons/bi';
import { GiMaterialsScience } from 'react-icons/gi';
import { FiExternalLink, FiX } from 'react-icons/fi';
import { FaRegClone } from 'react-icons/fa';
import { AiOutlineTag } from 'react-icons/ai';
import GrowthBadge from '../../Badge/GrowthBadge';
import PricePerCmIcon from 'components/CustomIcons/PricePerCmIcon';

export interface ListingAccordionProps {
  listing: ListingModel;
  onClick?: () => void;
  onListingClick?: (id: string, name: string) => void;
  onItemClick?: (id: string, name: string) => void;
  onArtistClick?: (id: string, name: string) => void;
  showAssociatedItem?: boolean;
  showAssociationAction?: boolean;
  onItemSuggestionClick?: (listing: ListingModel) => void;
  onTagListingClick?: (listing: ListingModel, action?: 'FAKE' | 'SET' | 'ORIGINAL') => void;
  onRemoveListingClick?: (listingId: string) => void;
  onUnlinkClick?: (listing: ListingModel) => void;
  unlinkLoading?: boolean;
  selected?: boolean;
  compactMode?: boolean;
  itemSuggestionLabel?: string;
}

export enum ListingAccordionSizingConfig {
  CARD_BORDER_WIDTH = '2px',
  IMAGE_WIDTH = '80px',
  IMAGE_HEIGHT = '80px',
  PANEL_MIN_HEIGHT = '80px'
}

export enum ListingAccordionStylingConfig {
  SIDE_BORDER_COLOR_LIGHT = '#f1f3f7',
  SIDE_BORDER_COLOR_DARK = '#3f434c'
}

export default function ListingAccordion({
  listing,
  onClick,
  onListingClick,
  onItemClick,
  onArtistClick,
  showAssociatedItem,
  showAssociationAction,
  onItemSuggestionClick,
  onTagListingClick,
  onRemoveListingClick,
  onUnlinkClick,
  unlinkLoading = false,
  selected,
  compactMode = false,
  itemSuggestionLabel = 'Suggest Item'
}: ListingAccordionProps) {
  const borderColor = useColorModeValue(ListingAccordionStylingConfig.SIDE_BORDER_COLOR_LIGHT, ListingAccordionStylingConfig.SIDE_BORDER_COLOR_DARK);
  const boxColor = useColorModeValue('gray.200', 'gray.800');
  const [showUnlinkConfirm, setShowUnlinkConfirm] = useState(false);

  function renderActionButton(label: string, icon: IconType, handler?: () => void, disabled: boolean = false, iconColor?: string) {
    const colorStyles = iconColor ? { color: iconColor } : {};
    return (
      <Tooltip label={label} key={`listing_action_${label}`}>
        <Flex
          as="button"
          w={6}
          h={6}
          align="center"
          justify="center"
          rounded="full"
          border="1px"
          cursor={disabled || !handler ? 'not-allowed' : 'pointer'}
          opacity={disabled || !handler ? 0.4 : 1}
          {...colorStyles}
          onClick={(event) => {
            event.stopPropagation();
            if (disabled || !handler) return;
            handler();
          }}
          _hover={disabled || !handler ? {} : { color: iconColor || 'blue.500' }}
        >
          <Icon as={icon} color={iconColor} />
        </Flex>
      </Tooltip>
    );
  }

  function renderUnlinkConfirmation() {
    return (
      <Flex gap={2} alignItems="center" marginTop={2}>
        <Text fontSize="xs" color="orange.600" fontWeight="medium">Unlink?</Text>
        <Tooltip label="Confirm Unlink">
          <Flex
            as="button"
            w={6}
            h={6}
            align="center"
            justify="center"
            rounded="full"
            border="1px"
            borderColor="green.500"
            color="green.500"
            cursor={unlinkLoading ? 'not-allowed' : 'pointer'}
            opacity={unlinkLoading ? 0.4 : 1}
            onClick={(event) => {
              event.stopPropagation();
              if (unlinkLoading) return;
              onUnlinkClick?.(listing);
              setShowUnlinkConfirm(false);
            }}
            _hover={unlinkLoading ? {} : { bg: 'green.50' }}
          >
            {unlinkLoading ? <Spinner size="xs" /> : <Icon as={MdCheck} />}
          </Flex>
        </Tooltip>
        <Tooltip label="Cancel">
          <Flex
            as="button"
            w={6}
            h={6}
            align="center"
            justify="center"
            rounded="full"
            border="1px"
            borderColor="gray.400"
            color="gray.500"
            cursor="pointer"
            onClick={(event) => {
              event.stopPropagation();
              setShowUnlinkConfirm(false);
            }}
            _hover={{ bg: 'gray.50' }}
          >
            <Icon as={FiX} />
          </Flex>
        </Tooltip>
      </Flex>
    );
  }

  function renderActionRow() {
    // If showing unlink confirmation, render that instead
    if (showUnlinkConfirm) {
      return renderUnlinkConfirmation();
    }

    const actions: (JSX.Element | null)[] = [];

    if (showAssociationAction && listing.processingStatus === ListingStatus.PENDING) {
      const hasAssociatedItem = listing.hasAssociatedItem();
      const isGroupingPending = listing.isGroupingPending();

      // In compact mode, show grouping and tag icons
      if (compactMode) {
        actions.push(
          renderActionButton(
            itemSuggestionLabel,
            MdGroupWork,
            !hasAssociatedItem && !isGroupingPending && onItemSuggestionClick
              ? () => onItemSuggestionClick(listing)
              : undefined,
            hasAssociatedItem || isGroupingPending
          )
        );
        actions.push(
          renderActionButton(
            'Tag Listing',
            AiOutlineTag,
            onTagListingClick ? () => onTagListingClick(listing) : undefined
          )
        );
      } else {
        // Normal mode: show individual tag buttons
        actions.push(
          renderActionButton(
            itemSuggestionLabel,
            MdGroupWork,
            !hasAssociatedItem && !isGroupingPending && onItemSuggestionClick
              ? () => onItemSuggestionClick(listing)
              : undefined,
            hasAssociatedItem || isGroupingPending
          )
        );
        actions.push(
          renderActionButton(
            'Mark as Fake',
            MdWarning,
            onTagListingClick ? () => onTagListingClick(listing, 'FAKE') : undefined
          )
        );
        actions.push(
          renderActionButton(
            'Mark as Set',
            MdFilter,
            onTagListingClick ? () => onTagListingClick(listing, 'SET') : undefined
          )
        );
        actions.push(
          renderActionButton(
            'Mark as Original',
            MdCropOriginal,
            onTagListingClick ? () => onTagListingClick(listing, 'ORIGINAL') : undefined
          )
        );
      }
    }

    // Unlink button - show when listing has associated item and handler is provided
    if (onUnlinkClick && listing.hasAssociatedItem()) {
      actions.push(
        renderActionButton(
          'Unlink from Item',
          MdLinkOff,
          () => setShowUnlinkConfirm(true),
          unlinkLoading,
          'orange.500'
        )
      );
    }

    if (listing.sourceLink) {
      actions.push(
        renderActionButton(
          'View Source',
          FiExternalLink,
          () => {
            window.open(listing.sourceLink, '_blank', 'noopener,noreferrer');
          },
          false
        )
      );
    }

    if (onRemoveListingClick) {
      actions.push(
        renderActionButton(
          'Remove Listing',
          BsTrashFill,
          () => onRemoveListingClick(listing.id),
          false,
          'red.500'
        )
      );
    }

    const visibleActions = actions.filter(Boolean);

    if (!visibleActions.length) {
      return null;
    }

    return (
      <Flex gap={2} marginTop={2} flexWrap="wrap" justifyContent={compactMode ? 'flex-start' : { base: "flex-start", md: "flex-end" }}>
        {visibleActions}
      </Flex>
    );
  }

  function renderListingLabels() {
    const labels = [];

    if (listing.isOriginal()) {
      labels.push(
        <Tag size={'sm'} key={'listingTag_original'} variant='solid' colorScheme={'pink'} width={'fit-content'}>
          Original
        </Tag>
      );
    }

    if (listing.isSet()) {
      labels.push(
        <Tag size={'sm'} key={'listingTag_set'} variant='solid' colorScheme={'teal'}>
          Set
        </Tag>
      );
    }

    if (!listing.isAuthentic()) {
      labels.push(
        <Tag size={'sm'} key={'listingTag_fake'} variant='solid' colorScheme={'red'}>
          Fake
        </Tag>
      );
    }

    if (listing.processingStatus === ListingStatus.PENDING) {
      labels.push(
        <Tag size={'sm'} key={'statusBadge_pending'} variant='solid' colorScheme={'gray'} width={'fit-content'}>
          Pending
        </Tag>
      );
    }

    // Rejected badge
    if (listing.processingStatus === ListingStatus.REJECTED) {
      labels.push(
        <Tag size={'sm'} key={'statusBadge_rejected'} variant='solid' colorScheme={'red'} width={'fit-content'}>
          Rejected
        </Tag>
      );
    }

    // Grouping pending badge
    if (listing.isGroupingPending()) {
      labels.push(
        <Tag size={'sm'} key={'statusBadge_groupingPending'} variant='solid' colorScheme={'yellow'} width={'fit-content'}>
          Grouping Pending
        </Tag>
      );
    }

    return (
      <Flex direction={'row'} gap={2} justifyContent={compactMode ? 'flex-start' : { base: 'flex-start', md: 'center' }}>
        {labels}
      </Flex>
    )
  }

  function renderListingSizingInfo() {
    if (listing.hasDimensions()) {
      return (
        <Flex direction={'row'} gap={1} alignItems="center">
          <Icon as={BiRuler} fontSize="xs" />
          <Text fontSize={'xs'}>{listing.getDimensionsForDisplay()}</Text>
        </Flex>
      );
    }

    return null;
  }

  function renderListingMaterialInfo() {
    if (!listing.hasMaterial()) {
      return null;
    }

    return (
      <Flex direction="row" gap={1} alignItems="center">
        <Icon as={GiMaterialsScience} fontSize="xs" />
        <Text fontSize="xs">{listing.getMaterialForDisplay()}</Text>
      </Flex>
    );
  }

  function renderListingEditionInfo() {
    if (!listing.hasEdition()) {
      return null;
    }

    return (
      <Flex direction="row" gap={1} alignItems="center">
        <Icon as={FaRegClone} fontSize="xs" />
        <Text fontSize="xs">{listing.getEditionForDisplay()}</Text>
      </Flex>
    );
  }

  function renderPricePeCm2Badge() {
    if (listing.isOriginal() && listing.hasDimensions() && !listing.isSet()) {
      const label = listing.getPricePerCm2ForDisplay();
      if (label) {
        return (
          <Tag fontSize={'xs'} fontWeight={'bold'} gap={1}>
            <Icon as={PricePerCmIcon} />
            {label}
          </Tag>
        );
      }
    }
  }

  function renderGrowthBadge() {
    const lastSaleChange = listing.getRawLastSalePercentageChange();
    if (listing.hasAssociatedItem()) {
      return (
        <Flex justifyContent={compactMode ? 'flex-start' : { base: "flex-start", md: "flex-end" }} marginTop={1}>
          <GrowthBadge percentage={lastSaleChange} />
        </Flex>
      );
    }
  }

  function renderListingArtist() {
    return (
      <Text width={'fit-content'} fontSize="xs" _hover={onArtistClick ? { color: "blue.500", cursor: "pointer" } : {}} onClick={() => onArtistClick?.(listing.artistId, listing.artist)}>{listing.artist}</Text>
    );
  }

  function renderListingPrice() {
    if (!listing.price.toLowerCase().includes('usd')) {
      return (
        <Text fontSize="xs"> • {listing.price}</Text>
      );
    }
  }

  function renderListingBox() {
    return (
      <Box
        width="100%"
        overflow='hidden'
        onClick={onClick}
        cursor={onClick ? 'pointer' : ''}
      >
        <Flex
          direction={compactMode ? 'row' : { base: 'column', md: 'row' }}
          gap={compactMode ? 4 : 0}
          alignItems={compactMode ? 'flex-start' : 'center'}
        >
          {/* Image Column */}
          <Flex direction="column" gap={compactMode ? 2 : 0} minWidth={ListingAccordionSizingConfig.IMAGE_WIDTH}>
            <Image
              loading="lazy"
              fit={'contain'}
              src={listing.image}
              fallbackSrc={`https://via.placeholder.com/${ListingAccordionSizingConfig.IMAGE_HEIGHT}`}
              height={ListingAccordionSizingConfig.IMAGE_HEIGHT}
              width={ListingAccordionSizingConfig.IMAGE_WIDTH}
            />
            {compactMode && (
              <>
                <Flex direction="column" gap={1}>
                  <Flex direction={'row'} wrap={'wrap'} gap={1} alignItems={'center'}>
                    <Heading fontSize="sm">{listing.getRawPriceForDisplay()}</Heading>
                    {renderListingPrice()}
                  </Flex>
                  {renderPricePeCm2Badge()}
                  {renderGrowthBadge()}
                </Flex>
                {renderActionRow()}
              </>
            )}
          </Flex>

          {/* Content Area */}
          <Flex
            width="100%"
            direction={compactMode ? 'column' : { base: 'column', md: 'row' }}
            marginLeft={compactMode ? 0 : '20px'}
            gap={compactMode ? 1 : 0}
          >
            {/* Title, Artist, Metadata */}
            <Box p={compactMode ? 0 : { base: 1, md: 6 }} width={compactMode ? '100%' : { base: '100%', md: '40%' }} textAlign={'left'}>
              <Box lineHeight='tight'>
                <Flex alignItems="center">
                  <Heading
                    fontSize="sm"
                    width={'fit-content'}
                    overflow={'nowrap'}
                    _hover={onListingClick ? { color: "blue.500", cursor: "pointer" } : {}}
                    onClick={(e) => {
                      if (onListingClick) {
                        e.stopPropagation();
                        onListingClick(listing.id, listing.getDisplayName());
                      }
                    }}
                  >
                    {listing.name}
                  </Heading>
                </Flex>
                {renderListingArtist()}
                {(() => {
                  const metadata = [
                    renderListingSizingInfo(),
                    renderListingMaterialInfo(),
                    renderListingEditionInfo(),
                  ].filter(Boolean);

                  if (!metadata.length) {
                    return null;
                  }

                  return (
                    <Stack spacing={1} marginTop={1}>
                      {metadata}
                    </Stack>
                  );
                })()}
              </Box>
            </Box>

            {/* Date, Source, Labels */}
            <Box p={compactMode ? 0 : { base: 1, md: 6 }} width={compactMode ? '100%' : { base: '100%', md: '30%' }} textAlign={compactMode ? 'left' : { base: 'left', md: 'center' }}>
              <Heading fontSize={compactMode ? 'xs' : { base: 'xs', md: 'sm' }} marginTop={compactMode ? 1 : 0}>{listing.getFormattedDate()}</Heading>
              <Text fontSize="xs">{listing.source}</Text>
              {renderListingLabels()}
            </Box>

            {/* Price and Actions (only in non-compact mode) */}
            {!compactMode && (
              <Box p={{ base: 1, md: 6 }} width={{ base: '100%', md: '30%' }} textAlign={{ base: 'left', md: 'right' }}>
                <Flex direction={'row'} wrap={'wrap'} gap={1} alignItems={'center'} justifyContent={{ base: 'flex-start', md: 'flex-end' }}>
                  <Heading fontSize="sm">{listing.getRawPriceForDisplay()}</Heading>
                  {renderListingPrice()}
                </Flex>
                <Flex direction='row' gap={1} alignItems={'center'} justifyContent={{ base: 'flex-start', md: 'flex-end' }}>
                  <Text fontSize="xs"> Realized Price </Text>
                  <Icon as={RiAuctionLine} w={3} h={3} />
                </Flex>
                {renderPricePeCm2Badge()}
                {renderGrowthBadge()}
                {renderActionRow()}
              </Box>
            )}
          </Flex>
        </Flex>
      </Box>
    );
  }

  function renderAssociatedItem() {
    const item = listing.getAssociatedItem();
    if (item) {
      return (
        <Box minHeight={ListingAccordionSizingConfig.PANEL_MIN_HEIGHT} paddingTop={2}>
          <Stack gap={2}>
            <Flex direction={'row'} gap={2} alignItems={'center'}>
              <Heading fontSize="sm">Associated Item</Heading>
              <Spacer />
              <Flex cursor={'pointer'} gap={1} alignItems={'center'} _hover={{ color: "blue.500" }} onClick={() => onItemClick?.(item.id, item.getDisplayName())}>
                <Heading fontSize="xs">See Item</Heading>
                <Icon as={BsFillArrowRightCircleFill} w={3} h={3} />
              </Flex>
            </Flex>
            {renderAssociatedItemData(item)}
          </Stack>
        </Box>
      );
    } else {
      return (
        <Box minHeight={ListingAccordionSizingConfig.PANEL_MIN_HEIGHT} paddingTop={2}>
          <Stack gap={4}>
            <Flex direction={'row'} gap={2} alignItems={'center'}>
              <Heading fontSize="sm">No Tagged Item</Heading>
            </Flex>
            {renderAssociatedItemEmptyContent()}
          </Stack>
        </Box>
      );
    }
  }

  function renderAssociatedItemEmptyContent() {
    if (listing.isGroupingPending()) {
      return (
        <Flex direction="row" alignItems="center" justifyContent="flex-start" color="gray.500">
          <Icon as={MdPendingActions} marginRight={2} width={8} height={8} />
          <Flex direction="column" textAlign="left" gap={2}>
            <Text fontSize="sm">Item Suggestion Pending Approval</Text>
          </Flex>
        </Flex>
      );
    } else {
      return (
        <Flex direction="row" alignItems="center" justifyContent="flex-start" color="gray.500">
          <Text fontSize="sm">No linked item yet.</Text>
        </Flex>
      );
    }
  }

  function renderItemImage(item: ItemModel) {
    return <Image fit={'contain'} marginRight={4} src={item.image} alt={item.name} width={ListingAccordionSizingConfig.IMAGE_WIDTH} height={ListingAccordionSizingConfig.IMAGE_HEIGHT} />;
  }

  function renderPricingData(item: ItemModel) {
    if (item.hasMarketSummaryData()) {
      return (
        <Stack>
          <Flex direction="row" gap={2} alignItems="center">
            <Flex direction={'column'} textAlign={'right'}>
              <Heading fontSize="sm">{item.getEstimatedMarketPriceForDisplay()}</Heading>
              <Text fontSize={'10px'}>Market Price</Text>
            </Flex>
            <Flex direction="row" gap={2} alignItems="center" justifyContent="flex-end" fontSize={'xs'}>
              <Flex
                padding={1}
                align={'center'}
                justify={'center'}
                rounded={'full'}
                border="1px">
                <Icon as={RiAuctionLine} w={3} h={3} />
              </Flex>
              {item.getSoldListingsQuantity()}
            </Flex>
          </Flex>
        </Stack>
      );
    }
  }

  function renderAssociatedItemData(item: ItemModel) {
    return (
      <Flex direction="row" alignItems="center" justifyContent="flex-start">
        {renderItemImage(item)}
        <Flex direction="column" textAlign="left" gap={1}>
          <Heading fontSize="sm" _hover={onItemClick ? { color: "blue.500", cursor: "pointer" } : {}} onClick={() => onItemClick?.(item.id, item.getDisplayName())}>{item.name}</Heading>
          {renderListingArtist()}
        </Flex>
        <Spacer />
        {renderPricingData(item)}
      </Flex>
    )
  }

  const shouldRenderAccordion = showAssociatedItem && listing.hasAssociatedItem();

  if (!shouldRenderAccordion) {
    return (
      <Box
        paddingLeft={4}
        paddingTop={4}
        paddingBottom={4}
        paddingRight={8}
        border={ListingAccordionSizingConfig.CARD_BORDER_WIDTH}
        borderColor={borderColor}
        backgroundColor={selected ? boxColor : ''}
        _hover={onClick ? { backgroundColor: boxColor } : {}}
      >
        {renderListingBox()}
      </Box>
    );
  }

  return (
    <Accordion
      allowToggle
      borderLeft={ListingAccordionSizingConfig.CARD_BORDER_WIDTH}
      borderLeftColor={borderColor}
      borderRight={ListingAccordionSizingConfig.CARD_BORDER_WIDTH}
      borderRightColor={borderColor}
      backgroundColor={selected ? boxColor : ''}
      _hover={onClick ? { backgroundColor: boxColor } : {}}
    >
      <AccordionItem>
        <AccordionButton>
          {renderListingBox()}
          <AccordionIcon />
        </AccordionButton>
        <AccordionPanel
          borderTop={ListingAccordionSizingConfig.CARD_BORDER_WIDTH}
          borderTopColor={borderColor}
        >
          {renderAssociatedItem()}
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
}
